import { useState, useEffect, useMemo } from "react";
import { Alert, Box, Container, Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { useOktaAuth } from "@okta/okta-react";
import SearchBar from "../search-bar/SearchBar";
import FileTree from "../file-tree/FileTree";
import FilePreview from "../file-preview/FilePreview";
import Loader from "../loader/Loader";
import Header from "../header/Header";

import {
  Cabinet,
  cabinets,
  CABINET_ACCESS_MAP,
} from "../../constants/cabinets.constant";

import "./Home.css";

import {
  FileApiData,
  FileApiToFetchFolder,
  FileNode,
} from "../../interfaces/api.interface";

import { Conf, useConf } from "../../context/ConfContext";

const Home: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { oktaAuth } = useOktaAuth();
  const { conf } = useConf();

  const [files, setFiles] = useState<FileNode[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileApiData | null>(null);
  const [showCabinetSelect, setShowCabinetSelect] = useState(false);
  const [userCabinetCode, setUserCabinetCode] = useState<string>("");
  const [networkPath, setNetworkPath] = useState<string>("");
  const [pathFetched, setPathFetched] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [cabinetName, setCabinetName] = useState<string>("");
  const [initExpandedItems, setInitExpandedItems] = useState(false);
  const [listCabinetsForSelect, setListCabinetsForSelect] = useState<Cabinet[]>(
    []
  );

  const bearerTokenStorage = localStorage.getItem("okta-token-storage");

  const bearerToken = useMemo(
    () => JSON.parse(bearerTokenStorage || "{}")?.accessToken,
    [bearerTokenStorage]
  );

  const user = bearerToken?.claims?.sub?.split("@")[0];
  const userCabinetPrincipal = bearerToken?.claims.cabinet_thetra;
  const userName = JSON.parse(bearerTokenStorage || "{}")?.idToken?.claims
    ?.name;

  const isAdmin = useMemo(
    () => (conf?.REACT_APP_ADMIN_LIST as string)?.includes(user),
    [conf, user]
  );

  const depth = useMemo(
    () => parseInt(conf?.REACT_APP_DEPTH_FOLDER as string) || 4,
    [conf]
  );

  const getCabinetNameByCodeCabinet = (codeCabinet: string): string => {
    const cabinet = cabinets.find((c) => c.ref === codeCabinet);
    return cabinet ? cabinet.name : "";
  };

  const resolveCabinets = (
    apiCabinets: string[],
    principal: string
  ): Cabinet[] => {
    const specialCabinets = CABINET_ACCESS_MAP[principal] || [];

    const allowedRefs = [...new Set([...apiCabinets, ...specialCabinets])];

    return cabinets.filter((c) => allowedRefs.includes(c.ref));
  };

  useEffect(() => {
    const fetchUserCabinets = async () => {
      setLoading(true);
      try {
        const url = conf?.REACT_APP_GED_THETRA_API_URL;
        if (!url) return;

        const response = await fetch(`${url}/user/cabinets`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken.accessToken}`, // <-- dépendance
          },
        });

        if (response.status === 401) {
          await oktaAuth.signOut(); // <-- dépendance
          return;
        }

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data: string[] = await response.json();

        const allowedCabinets: Cabinet[] = isAdmin
          ? cabinets
          : resolveCabinets(data, userCabinetPrincipal);

        if (allowedCabinets.length === 0) {
          enqueueSnackbar(
            "Vous n'avez pas les habilitations nécessaires", // <-- dépendance
            { variant: "error" }
          );
        }

        setListCabinetsForSelect(allowedCabinets);
        setShowCabinetSelect(allowedCabinets.length > 1);
      } catch (error) {
        console.error("Error fetching cabinets:", error);
        enqueueSnackbar("Échec du chargement des cabinets.", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserCabinets();

    setUserCabinetCode(userCabinetPrincipal);
    setCabinetName(getCabinetNameByCodeCabinet(userCabinetPrincipal));
    setFiles([]);
  }, [
    conf,
    isAdmin,
    userCabinetPrincipal,
    bearerToken.accessToken,
    oktaAuth,
    enqueueSnackbar,
  ]);

  const handleSearch = async (
    path: string,
    codeCabinet: string = userCabinetCode
  ) => {
    path = path.replace(/\\$/, "").trim();

    setLoading(true);
    setFiles([]);
    setSelectedFile(null);
    setNetworkPath(path);
    setPathFetched([]);
    setInitExpandedItems(true);

    try {
      const encodedPath = encodeURIComponent(path);

      setUserCabinetCode(codeCabinet);

      const bearerTokenStorage = localStorage.getItem("okta-token-storage");
      const bearerToken = JSON.parse(bearerTokenStorage || "{}")?.accessToken;

      if (!bearerToken) {
        throw new Error("No token found");
      }

      const response = await fetch(
        `${conf?.REACT_APP_GED_THETRA_API_URL}/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken.accessToken}`,
          },
          body: JSON.stringify({ path: encodedPath, codeCabinet }),
        }
      );

      if (response.status === 401) {
        await oktaAuth.signOut();
        return;
      }

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (data.length === 0) {
        setFiles([]);
        enqueueSnackbar("Aucun fichier trouvé.", { variant: "warning" });
      } else {
        setPathFetched((prev) => [...prev, path]);
        setFiles(data);

        if (data.length === 1) {
          setSelectedFile(data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching files:", error);

      setFiles([]);

      enqueueSnackbar("Échec du chargement des fichiers. Veuillez réessayer.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
      setInitExpandedItems(false);
    }
  };

  const handleSelectFile = async (file: FileApiToFetchFolder) => {
    const newPath = networkPath.replace(/\\$/, "") + "\\" + file.file;

    const isFetchedAnyWhere = pathFetched.filter((path) =>
      path.includes(file.file)
    );

    if (
      file.url === "" &&
      !pathFetched.includes(newPath) &&
      isFetchedAnyWhere.length === 0
    ) {
      setLoading(true);

      try {
        const encodedPath = encodeURIComponent(file.urlFetch);

        const bearerTokenStorage = localStorage.getItem("okta-token-storage");
        const bearerToken = JSON.parse(bearerTokenStorage || "{}")?.accessToken;

        if (!bearerToken) {
          throw new Error("No token found");
        }

        const response = await fetch(
          `${(conf as Conf).REACT_APP_GED_THETRA_API_URL}/list`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${bearerToken.accessToken}`,
            },
            body: JSON.stringify({
              path: encodedPath,
              codeCabinet: userCabinetCode,
            }),
          }
        );

        if (response.status === 401) {
          await oktaAuth.signOut();
          return;
        }

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();

        if (data.length === 0) {
          enqueueSnackbar("Aucun fichier trouvé dans ce dossier.", {
            variant: "warning",
          });
        } else {
          setPathFetched((prev) => [...prev, newPath]);
          setFiles((prev) => [...prev, ...data]);
        }
      } catch (error) {
        console.error("Error fetching files:", error);

        enqueueSnackbar("Échec du chargement des fichiers du dossier.", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedFile(file);
    }
  };

  return (
    <>
      <Container sx={{ marginBottom: 4 }}>
        <Header user={userName} cabinet={cabinetName} />

        <SearchBar
          onSearch={handleSearch}
          cabinets={listCabinetsForSelect}
          showCabinetSelect={showCabinetSelect}
          defaultCabinetCode={userCabinetCode}
          depthFolders={depth}
        />

        {loading && <Loader />}

        <Grid container spacing={3} mt={2}>
          {files.length <= 0 && (
            <Grid item xs={12} md={12}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Alert
                  variant="outlined"
                  severity="warning"
                  sx={{ width: "auto" }}
                >
                  Renseignez le chemin de la GED Thetra afin de visualiser le
                  contenu
                </Alert>
              </Box>

              <Box
                sx={{
                  height: "45%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  component="img"
                  alt="sélection obligatoire default img"
                  src="selectionObligatoire.png"
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <FileTree
              files={files}
              networkPath={networkPath}
              codeCabinet={userCabinetCode}
              initExpandedItems={initExpandedItems}
              onSelect={handleSelectFile}
            />
          </Grid>

          <Grid item xs={12} md={9}>
            <FilePreview
              displayPreview={files.length > 0}
              file={selectedFile}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Home;
